import { Button, Card, Col, Form, InputGroup, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import PaginationComponent from "../../Components/Pagination";
import ShowRecordItem from "../../Components/ShowRecordItem";
import NoRecordFound from "../../Components/NoRecordFound";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { WhiteLabelWebsitePropTypes } from "../../PropTypes/WebsitePropTypes";
import LogoPreview from "../../Assets/img/logo-placeholder-image.webp";
import { debounce } from "lodash";
import { UpdateWhiteLabelServiceDomainStatus, getWhiteLabelServiceDomainList, updateBrandDetailsService } from "../../Services/WhitelabelActionService";

import "../../Assets/scss/components/custom-branding-service.scss";

// component to render custom branding page
const CustomBrandingService = () => {

    // Functions
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Redux
    const { accessToken, currentWebsite, user } = useSelector((store: StoreProptypes) => store);

    // State
    const [loading, setLoading] = useState(false);
    const [loadingLogo, setLoadingLogo] = useState(false);
    const [loadingURL, setLoadingURL] = useState(false);

    const [customBrndLogo, setCustomBrndLogo] = useState<File>();
    const [customBrndLogoValidation, setCustomBrndLogoValidation] = useState<boolean>(true);
    const [customBrndLogoURL, setCustomBrndLogoURL] = useState<string | null>();
    const [customBrndLogoSize, setCustomBrndLogoSize] = useState<number>(0);
    const [customBrndLogoURLPreviewToggle, setCustomBrndLogoURLPreviewToggle] = useState(false);
    const [customBrndLogoURLPreview, setCustomBrndLogoURLPreview] = useState<string | null>();
    const [customBrndLogoError, setCustomBrndLogoError] = useState(false);
    const [customBrndLogoName, setCustomBrndLogoName] = useState('');
    const [customBrndURL, setCustomBrndURL] = useState('');

    const [customBrndLogoFiledValidation, setCustomBrndLogoFiledValidation] = useState<boolean>(false);
    const [customBrndLogoValidationMsg, setCustomBrndLogoValidationMsg] = useState('');
    const [customBrndURLValidation, setCustomBrndURLValidation] = useState<boolean>(false);
    const [customBrndURLValidationMsg, setCustomBrndURLValidationMsg] = useState('');

    const [apiRecall, setApiRecall] = useState<boolean>(false);

    const [activeWebsiteList, setActiveWebsiteList] = useState<WhiteLabelWebsitePropTypes[]>([]);
    const [activeWebsiteApiLoader, setActiveWebsiteApiLoader] = useState<boolean>(false)
    const [activeDomainTotalRecord, setActiveDomainTotalRecord] = useState<number>(0);
    const [activeDomainMainTotalRecord, setActiveDomainMainTotalRecord] = useState<number>(0);
    const [activeDomainCurrentPage, setActiveDomainCurrentPage] = useState<number>(1);
    const [activeDomainLimit, setActiveDomainLimit] = useState<number>(10);
    const [activeDomainOffset, setActiveDomainOffset] = useState<number>(0);
    const [activeDomainTerms, setActiveDomainTerms] = useState<string>("");
    const [domainFilter, setDomainFilter] = useState<number>(4);


    // function to handle file input change
    const changeHandler = (e: any) => {
        const target = e.target;
        const fileUploaded = target.files;

        // check if files selected
        if (e.target.files[0] !== undefined) {
            // check if selected file format is svg or png
            if ((e.target.files[0].type === 'image/svg+xml' || e.target.files[0].type === 'image/png')) {
                setCustomBrndLogoValidation(false);
                setCustomBrndLogoError(false);
                setCustomBrndLogoSize(e.target.files[0].size);
                const fileSize = parseFloat((((e.target.files[0].size) / 1024) / 1024).toFixed(2));

                // check if file size is more than 2MB
                if ((fileSize > 2)) {
                    setCustomBrndLogoFiledValidation(true);
                    setLoadingLogo(true);
                    setCustomBrndLogoValidationMsg('The maximum allowed file size is 2MB.')
                }
                else {
                    setLoadingLogo(false);
                    setCustomBrndLogoFiledValidation(false)
                    setCustomBrndLogo(e.target.files[0]);
                    setCustomBrndLogoName(e.target.files[0].name);

                    // check if uploaded files are available
                    if (fileUploaded) {
                        let reader = new FileReader();
                        reader.readAsDataURL(fileUploaded[0]);

                        reader.onload = (e) => {
                            // check if base64 string is available
                            if (reader && typeof reader.result === "string") {
                                setCustomBrndLogoURLPreview(reader.result);
                                setCustomBrndLogoURLPreviewToggle(true);
                            }
                        }
                    }
                }
            } else {
                setCustomBrndLogoError(true);
                setCustomBrndLogoFiledValidation(true);
                setLoadingLogo(true);
                setCustomBrndLogoValidationMsg('Please upload png or svg image.')
            }
        }

    };

    useEffect(() => {
        // check if user data is available
        if (user) {
            // check if user is a partner user
            if (user.department === 4) {
                // check if branding logo is available
                if (user.brand_logo !== '' && user.brand_logo !== null && user.brand_logo !== undefined) {
                    setCustomBrndLogoURL(user.brand_logo);
                    setCustomBrndLogoValidation(false);
                } else {
                    setCustomBrndLogoURL(null);
                    setCustomBrndLogoValidation(true);
                }
                // check if website link is available
                if (user.website !== null) {
                    setCustomBrndURL(user.website);
                } else {
                    setCustomBrndURL('');
                }
            } else {

            }
        }
    }, [user]);


    const prevLimitRef = useRef<number>(10);

    useEffect(() => {
        setActiveDomainCurrentPage(1);
    }, [activeDomainLimit, activeDomainTerms, domainFilter]);


    useEffect(() => {
        setActiveDomainOffset(0);
    }, [activeDomainTerms, domainFilter]);


    useEffect(() => {
        setActiveDomainOffset(activeDomainCurrentPage * activeDomainLimit - activeDomainLimit);
    }, [activeDomainCurrentPage, activeDomainLimit]);

    // function to handle search
    const searchActiveDomainChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setActiveDomainTerms(e.target.value);
    };

    // throttle function call on user input
    const debouncedSearchActiveDomainHandler = useMemo(() => {
        return debounce(searchActiveDomainChangeHandler, 500);
    }, []);


    useEffect(() => {
        // check if access token exists and limit per page is unchanged
        if (accessToken && ((activeDomainLimit === prevLimitRef.current))) {
            // service to get domain list for white label
            getWhiteLabelServiceDomainList(accessToken, 6, 1, activeDomainLimit, activeDomainOffset, activeDomainTerms, setActiveDomainTotalRecord, setActiveDomainMainTotalRecord, setActiveWebsiteList, setActiveWebsiteApiLoader);
        }
    }, [activeDomainOffset, activeDomainTerms, accessToken]);

    useEffect(() => {
        // check if limit is unchanged and offset is 0
        if ((activeDomainLimit !== prevLimitRef.current) && activeDomainOffset === 0) {
            // check if access token exist
            if (accessToken) {
                // service to get domain list for white label
                getWhiteLabelServiceDomainList(accessToken, 6, 1, activeDomainLimit, activeDomainOffset, activeDomainTerms, setActiveDomainTotalRecord, setActiveDomainMainTotalRecord, setActiveWebsiteList, setActiveWebsiteApiLoader);
            }
            prevLimitRef.current = activeDomainLimit;
        }
    }, [activeDomainOffset, activeDomainLimit, domainFilter]);


    // white label Service For Inactive Domain 
    const [inactiveWebsiteList, setInactiveWebsiteList] = useState<WhiteLabelWebsitePropTypes[]>([]);
    const [inactiveWebsiteApiLoader, setInactiveWebsiteApiLoader] = useState<boolean>(false)
    const [inactiveDomainTotalRecord, setInactiveDomainTotalRecord] = useState<number>(0);
    const [inactiveDomainMainTotalRecord, setInactiveDomainMainTotalRecord] = useState<number>(0);
    const [inactiveDomainCurrentPage, setInactiveDomainCurrentPage] = useState<number>(1);
    const [inactiveDomainLimit, setInactiveDomainLimit] = useState<number>(10);
    const [inactiveDomainOffset, setInactiveDomainOffset] = useState<number>(0);
    const [inactiveDomainTerms, setInactiveDomainTerms] = useState<string>("");
    const prevInactiveLimitRef = useRef<number>(10);


    useEffect(() => {
        setInactiveDomainCurrentPage(1);
    }, [inactiveDomainLimit, inactiveDomainTerms]);


    useEffect(() => {
        setInactiveDomainOffset(0);
    }, [activeDomainTerms]);


    useEffect(() => {
        setInactiveDomainOffset(inactiveDomainCurrentPage * inactiveDomainLimit - inactiveDomainLimit);
    }, [inactiveDomainCurrentPage, inactiveDomainLimit]);


    // function to search inactive domains
    const searchInActiveDomainChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInactiveDomainTerms(e.target.value);
    };

    // throttle function call on user input
    const debouncedSearchInActiveDomainHandler = useMemo(() => {
        return debounce(searchInActiveDomainChangeHandler, 500);
    }, []);


    useEffect(() => {
        // check if access token is available and limit is unchanged
        if (accessToken && ((inactiveDomainLimit === prevInactiveLimitRef.current))) {
            getWhiteLabelServiceDomainList(accessToken, 6, 0, inactiveDomainLimit, inactiveDomainOffset, inactiveDomainTerms, setInactiveDomainTotalRecord, setInactiveDomainMainTotalRecord, setInactiveWebsiteList, setInactiveWebsiteApiLoader);
        }
    }, [inactiveDomainOffset, inactiveDomainTerms, accessToken]);


    useEffect(() => {
        // check if limit is unchanged and offset is set to 0
        if ((inactiveDomainLimit !== prevInactiveLimitRef.current) && inactiveDomainOffset === 0) {
            // check if access token is available
            if (accessToken) {
                // service to get white label domain list
                getWhiteLabelServiceDomainList(accessToken, 6, 0, inactiveDomainLimit, inactiveDomainOffset, inactiveDomainTerms, setInactiveDomainTotalRecord, setInactiveDomainMainTotalRecord, setInactiveWebsiteList, setInactiveWebsiteApiLoader);
            }
            prevInactiveLimitRef.current = inactiveDomainLimit;
        }
    }, [inactiveDomainOffset, inactiveDomainLimit, domainFilter]);


    useEffect(() => {
        // check if api recall flag and access token is available
        if (apiRecall && accessToken) {
            getWhiteLabelServiceDomainList(accessToken, 6, 1, activeDomainLimit, activeDomainOffset, activeDomainTerms, setActiveDomainTotalRecord, setActiveDomainMainTotalRecord, setActiveWebsiteList, setActiveWebsiteApiLoader);
            getWhiteLabelServiceDomainList(accessToken, 6, 0, inactiveDomainLimit, inactiveDomainOffset, inactiveDomainTerms, setInactiveDomainTotalRecord, setInactiveDomainMainTotalRecord, setInactiveWebsiteList, setInactiveWebsiteApiLoader);
        }
    }, [apiRecall])

    // Array merge
    const [finalArray, setFinalArray] = useState<WhiteLabelWebsitePropTypes[]>([])
    const [selectedWebsiteList, setSelectedWebsiteList] = useState<WhiteLabelWebsitePropTypes[]>([]);


    useEffect(() => {
        // check if active website list or inactive website list is available
        if (activeWebsiteList || inactiveWebsiteList) {
            setFinalArray([...activeWebsiteList, ...inactiveWebsiteList]);
            setApiRecall(false);
        }
    }, [activeWebsiteList, inactiveWebsiteList])


    // function to merge two arrays
    const mergeArray = (
        A: WhiteLabelWebsitePropTypes[], // array 1
        B: WhiteLabelWebsitePropTypes[] // array 2
    ) => {
        let mergedArray = [...A, ...B];
        return mergedArray;
    };

    // function to delete B array items from A array
    const popArray = (
        A: WhiteLabelWebsitePropTypes[], // array 1
        B: WhiteLabelWebsitePropTypes[] // array 2
    ) => {
        const tempArray = A.filter((item) => {
            return !B.some((Bitem) => item.id === Bitem.id);
        });
        return tempArray;
    };

    // function to handle white label active status
    const HandleWhiteLabelStatus = (
        item: WhiteLabelWebsitePropTypes, // website object
        flag: Boolean, // radio checked or unchecked
        status: number // flag whether its from active website list or inactive website list
    ) => {
        // check if its from active website list
        if (status === 1) {
            // check if radio is checked
            if (flag) {
                setSelectedWebsiteList(mergeArray(selectedWebsiteList, [{ ...item, adon_status: 0 }]))
            } else {
                setSelectedWebsiteList(popArray(selectedWebsiteList, [item]))
            }
        } 
        else {
            // check if radio is checked
            if (flag) {
                setSelectedWebsiteList(mergeArray(selectedWebsiteList, [{ ...item, adon_status: 1 }]))
            } else {
                setSelectedWebsiteList(popArray(selectedWebsiteList, [item]))
            }
        }
    }


    // function to handle form submit
    const HandleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        var httpRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;
        // check if custom branding logo is validated
        if (customBrndLogoValidation) {
            setCustomBrndLogoFiledValidation(true);
            return setCustomBrndLogoValidationMsg('Please upload logo.');
        } 
        // check if website url is empty
        else if (customBrndURL === '') {
            setCustomBrndURLValidation(true);
            return setCustomBrndURLValidationMsg("Please enter logo URL.");
        } 
        // check if website url is invalid
        else if (!httpRegex.test(customBrndURL)) {
            setCustomBrndURLValidation(true);
            return setCustomBrndURLValidationMsg("Please enter valid URL.");
        } else {
            // check if access token, current website and user data are available
            if (accessToken && currentWebsite && user) {
                // service to update custom branding
                updateBrandDetailsService(accessToken, user.id, customBrndURL, setLoading, dispatch, t, customBrndLogo);
                setCustomBrndLogoName('');
            }
        }
    }

    // function to handle white label status update
    const HandleWhiteLabelStatusUpdate = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        // check if logo and url are available
        if (customBrndURL || customBrndLogo) {
            HandleSubmit(e);
        }

        // check if access token, selected website list are available
        if (accessToken && selectedWebsiteList && selectedWebsiteList.length > 0) {
            // service to update white label status
            UpdateWhiteLabelServiceDomainStatus(accessToken, 6, selectedWebsiteList, setApiRecall, dispatch, t, setSelectedWebsiteList);
        }
    }

    // function to validate website url
    const URLValidationEvent = (e: any) => {
        const inputValue = e.target.value;
        let httpRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;

        // check if input is empty
        if (inputValue === '') {
            setCustomBrndURLValidation(true);
            setLoadingURL(true);
            setCustomBrndURLValidationMsg("Please enter logo URL.");
        } 
        // check if url is valid
        else if (!httpRegex.test(inputValue)) {
            setCustomBrndURLValidation(true);
            setLoadingURL(true);
            setCustomBrndURLValidationMsg("Please enter valid URL like https://skynettechnologies.com or http://skynettechnologies.com.");
        }
        else {
            setCustomBrndURLValidation(false);
            setLoadingURL(false);
        }

        setCustomBrndURL(inputValue);
    }

    return (
        <>
            <div className="aioa_dashboard-custom-branding-service-wrapper">

                <div className="aioa_dashboard-custom-branding-service-logo-upload-wrapper">
                    <Form>
                        <Row xs={1} md={1} lg={2} xl={2}>
                            <Col>
                                <Card>
                                    <Card.Body>
                                        <div className="logo-preview-main">
                                            <div className="logo-preview">
                                                {customBrndLogoURLPreviewToggle ? (
                                                    <>
                                                        <img src={customBrndLogoURLPreview ? customBrndLogoURLPreview : LogoPreview} />
                                                    </>
                                                ) : (
                                                    <>

                                                        <img src={customBrndLogoURL ? `${process.env.REACT_APP_BASE_URL}/storage/app/brand_logos/${customBrndLogoURL}` : LogoPreview} alt={t("Logo preview").toString()} />
                                                    </>
                                                )}
                                            </div>
                                            <div className="logo-upload">
                                                <div className="logo-upload-inner">
                                                    <div className="logo-upload-input">
                                                        <Form.Label htmlFor="upload_file" className="btn btn-primary">{t("Upload Logo")}</Form.Label>
                                                        <InputGroup className="input-group-outline ">
                                                            <Form.Control id="upload_file" type="file" aria-describedby="file_input_description" onChange={(e) => changeHandler(e)} className="ps-0" />
                                                        </InputGroup>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="file-url mt-2">{customBrndLogoName}</div>

                                        <div id="file_input_description">
                                            {customBrndLogoFiledValidation && (
                                                <>
                                                    <div className="mt-3">
                                                        <strong className="text-danger">{customBrndLogoValidationMsg}</strong>
                                                    </div>
                                                </>
                                            )}

                                            <div className="mt-3">
                                                <strong>{t("Accepted File Formats")}</strong>: png, svg. <br />
                                                <strong>{t("Recommended image dimensions")}</strong>: 300px X 125px
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col>
                                <Card>
                                    <Card.Body>
                                        <div className="logo-upload">
                                            <Form.Label htmlFor="logo_url"><strong>{t("Logo URL")}</strong></Form.Label>
                                            <Form.Control aria-describedby="logo_url_description" size="lg" id="logo_url" type="text" defaultValue={customBrndURL} onChange={(e) => URLValidationEvent(e)} onBlur={(e) => URLValidationEvent(e)} />
                                            <div id="logo_url_description">
                                                {customBrndURLValidation && (
                                                    <>
                                                        <div className="mt-2">
                                                            <strong className="text-danger">{customBrndURLValidationMsg}</strong>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>

                <div className="aioa_dashboard-custom-branding-service-domain-list-wrapper">
                    <Row xl={2}>
                        <Col>
                            <Card>
                                <Card.Header>
                                    <Card.Title className="mb-0">{t("Select domain to remove custom branding service")}</Card.Title>
                                </Card.Header>
                                <Card.Body>
                                    <div className="aioa_dashboard-custom-branding-service-domain-list-table">
                                        <div className={`aioa_dashboard-order-list-filter-wrapper`} id="aioa_dashboard-order-list-filter">

                                            <div className="aioa_dashboard-order-list-filter-block search-block">
                                                <Form.Label className='d-lg-none'>{t("Search")}</Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        placeholder={t("Search Domain").toString()}
                                                        aria-label={t("Search Domain").toString()}
                                                        type="text"
                                                        onChange={debouncedSearchActiveDomainHandler}
                                                        disabled={activeDomainMainTotalRecord > 0 ? false : true}
                                                    />
                                                    <InputGroup.Text><i className="material-symbols-outlined" aria-hidden="true">search</i></InputGroup.Text>
                                                </InputGroup>
                                            </div>

                                            <div className="aioa_dashboard-order-list-filter-block">
                                                <Form.Label htmlFor='showPages'>{t("Items Per Page")}</Form.Label>
                                                <Form.Select
                                                    onChange={(e) => setActiveDomainLimit(parseInt(e.target.value))}
                                                    id="items_per_page"
                                                    defaultValue={activeDomainLimit}
                                                    disabled={activeDomainMainTotalRecord > 0 ? false : true}
                                                >
                                                    <option value="10" selected={(activeDomainLimit === 10) ? true : false}>{t("10")}</option>
                                                    <option value="20" selected={(activeDomainLimit === 20) ? true : false}>{t("20")}</option>
                                                    <option value="40" selected={(activeDomainLimit === 40) ? true : false}>{t("40")}</option>
                                                    <option value="80" selected={(activeDomainLimit === 80) ? true : false}>{t("80")}</option>
                                                    <option value="100" selected={(activeDomainLimit === 100) ? true : false}>{t("100")}</option>
                                                </Form.Select>
                                            </div>

                                        </div>

                                        <div className="aioa_dashboard-table">
                                            <div className="aioa_dashboard-table-thead">
                                                <div className="aioa_dashboard-table-tr">
                                                    <div className="aioa_dashboard-table-th tcw-50 aioa_dashboard-table-cell-sidegap">&nbsp;</div>
                                                    <div className="aioa_dashboard-table-th tcw-auto aioa_dashboard-table-cell-sidegap">{t("Domain")}</div>
                                                </div>
                                            </div>
                                            <div className="aioa_dashboard-table-body" aria-live='polite'>
                                                {!(activeWebsiteApiLoader) ? (
                                                    <>
                                                        {activeWebsiteList && activeWebsiteList.length > 0 ? (
                                                            <>
                                                                {activeWebsiteList.map((activeItem, index) => {
                                                                    return (
                                                                        <div className={`aioa_dashboard-table-tr`} key={index + 1 + activeDomainOffset}>

                                                                            <div className="aioa_dashboard-table-td tcw-50 aioa_dashboard-table-cell-sidegap">
                                                                                <div className='aioa_dashboard-table-td-cell-title d-none' aria-hidden="true">{t("Select Website")}</div>
                                                                                <div className='aioa_dashboard-table-td-cell-value' aria-label={t("Select Website").toString()}>
                                                                                    <Form.Check
                                                                                        className="p-0 m-0 form-check"
                                                                                        type="checkbox"
                                                                                        aria-label={t("Select domain").toString()}
                                                                                        name={`${activeItem.id}`}
                                                                                        id={`${activeItem.id}`}
                                                                                        onChange={(e) => HandleWhiteLabelStatus(activeItem, e.target.checked, 1)}
                                                                                        defaultChecked={selectedWebsiteList.filter((selectedItem) => selectedItem.id === activeItem.id).length > 0 ? true : false}
                                                                                    />
                                                                                </div>
                                                                            </div>

                                                                            <div className="aioa_dashboard-table-td tcw-auto aioa_dashboard-table-cell-sidegap">
                                                                                <div className='aioa_dashboard-table-td-cell-title d-none' aria-hidden="true">{t("Domain")}</div>
                                                                                <div className='aioa_dashboard-table-td-cell-value' aria-label={t("Domain").toString()}>
                                                                                    <div className="domain-name" style={{ fontWeight: 500 }}>
                                                                                        <OverlayTrigger
                                                                                            overlay={<Tooltip id="selected_domain_tooltip">
                                                                                                <div className="text-nowrap" style={{ maxWidth: 300 }}>
                                                                                                    {activeItem.url}
                                                                                                </div>
                                                                                            </Tooltip>}
                                                                                        >
                                                                                            <span>{activeItem.url}</span>
                                                                                        </OverlayTrigger>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className='p-4'>
                                                                    <NoRecordFound imageType={`domain`} joyful={false} noRecordText={`${t('Domain not found')}`} />
                                                                </div>
                                                            </>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>

                                                    </>
                                                )}
                                            </div>
                                            {activeWebsiteList && activeWebsiteList.length > 0 && (
                                                <div className="aioa_dashboard-table-pagination-main">
                                                    <>
                                                        <ShowRecordItem
                                                            offset={activeDomainOffset}
                                                            limit={activeDomainLimit}
                                                            totalRecord={activeDomainTotalRecord}
                                                        />
                                                        <PaginationComponent
                                                            currentPage={activeDomainCurrentPage}
                                                            totalRecords={activeDomainTotalRecord}
                                                            itemsPerPage={activeDomainLimit}
                                                            setCurrentPage={setActiveDomainCurrentPage}
                                                        />
                                                    </>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col>
                            <Card>
                                <Card.Header>
                                    <Card.Title className="mb-0">{t("Select domain to add custom branding service")}</Card.Title>
                                </Card.Header>
                                <Card.Body>
                                    <div className="aioa_dashboard-custom-branding-service-domain-list-table">

                                        <div className={`aioa_dashboard-order-list-filter-wrapper`} id="aioa_dashboard-order-list-filter">

                                            <div className="aioa_dashboard-order-list-filter-block search-block">
                                                <Form.Label className='d-lg-none'>{t("Search")}</Form.Label>
                                                <InputGroup className="input-group-outline search-form-nobtn">
                                                    <Form.Control
                                                        placeholder={t("Search Domain").toString()}
                                                        aria-label={t("Search Domain").toString()}
                                                        type="text"
                                                        onChange={debouncedSearchInActiveDomainHandler}
                                                        disabled={inactiveDomainMainTotalRecord > 0 ? false : true}
                                                    />
                                                    <InputGroup.Text><i className="material-symbols-outlined" aria-hidden="true">search</i></InputGroup.Text>
                                                </InputGroup>
                                            </div>

                                            <div className="aioa_dashboard-order-list-filter-block">
                                                <Form.Label htmlFor='showPages'>{t("Items Per Page")}</Form.Label>
                                                <Form.Select
                                                    onChange={(e) => setInactiveDomainLimit(parseInt(e.target.value))}
                                                    id="items_per_page_1"
                                                    defaultValue={inactiveDomainLimit}
                                                    disabled={inactiveDomainMainTotalRecord > 0 ? false : true}
                                                >
                                                    <option value="10" selected={(inactiveDomainLimit === 10) ? true : false}>{t("10")}</option>
                                                    <option value="20" selected={(inactiveDomainLimit === 20) ? true : false}>{t("20")}</option>
                                                    <option value="40" selected={(inactiveDomainLimit === 40) ? true : false}>{t("40")}</option>
                                                    <option value="80" selected={(inactiveDomainLimit === 80) ? true : false}>{t("80")}</option>
                                                    <option value="100" selected={(inactiveDomainLimit === 100) ? true : false}>{t("100")}</option>
                                                </Form.Select>
                                            </div>

                                        </div>
                                        <div className="aioa_dashboard-table">
                                            <div className="aioa_dashboard-table-thead">
                                                <div className="aioa_dashboard-table-tr">
                                                    <div className="aioa_dashboard-table-th tcw-50 aioa_dashboard-table-cell-sidegap">&nbsp;</div>
                                                    <div className="aioa_dashboard-table-th tcw-auto aioa_dashboard-table-cell-sidegap">{t("Domain")}</div>
                                                </div>
                                            </div>
                                            <div className="aioa_dashboard-table-body" aria-live='polite'>
                                                {!(inactiveWebsiteApiLoader) ? (
                                                    <>
                                                        {inactiveWebsiteList && inactiveWebsiteList.length > 0 ? (
                                                            <>
                                                                {inactiveWebsiteList.map((activeItem, index) => {
                                                                    return (
                                                                        <div className={`aioa_dashboard-table-tr`} key={index + 1 + activeDomainOffset}>

                                                                            <div className="aioa_dashboard-table-td tcw-50 aioa_dashboard-table-cell-sidegap">
                                                                                <div className='aioa_dashboard-table-td-cell-title d-none' aria-hidden="true">{t("Select Website")}</div>
                                                                                <div className='aioa_dashboard-table-td-cell-value' aria-label={t("Select Website").toString()}>
                                                                                    <Form.Check
                                                                                        className="p-0 m-0 form-check"
                                                                                        type="checkbox"
                                                                                        aria-label={t("Select domain").toString()}
                                                                                        name={`${activeItem.id}`}
                                                                                        id={`${activeItem.id}`}
                                                                                        onChange={(e) => HandleWhiteLabelStatus(activeItem, e.target.checked, 0)}
                                                                                        defaultChecked={selectedWebsiteList.filter((selectedItem) => selectedItem.id === activeItem.id).length > 0 ? true : false}
                                                                                    />
                                                                                </div>
                                                                            </div>

                                                                            <div className="aioa_dashboard-table-td tcw-auto aioa_dashboard-table-cell-sidegap">
                                                                                <div className='aioa_dashboard-table-td-cell-title d-none' aria-hidden="true">{t("Domain")}</div>
                                                                                <div className='aioa_dashboard-table-td-cell-value' aria-label={t("Domain").toString()}>

                                                                                    <div className="domain-name" style={{ fontWeight: 500 }}>
                                                                                        <OverlayTrigger
                                                                                            overlay={<Tooltip id="selected_domain_tooltip">
                                                                                                {activeItem.url}
                                                                                            </Tooltip>}
                                                                                        >
                                                                                            <span>{activeItem.url}</span>
                                                                                        </OverlayTrigger>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className='p-4'>
                                                                    <NoRecordFound imageType={`domain`} joyful={false} noRecordText={`${t('Domain not found')}`} />
                                                                </div>
                                                            </>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>

                                                    </>
                                                )}
                                            </div>
                                            {inactiveWebsiteList && inactiveWebsiteList.length > 0 && (
                                                <div className="aioa_dashboard-table-pagination-main">
                                                    <>
                                                        <ShowRecordItem
                                                            offset={inactiveDomainOffset}
                                                            limit={inactiveDomainLimit}
                                                            totalRecord={inactiveDomainTotalRecord}
                                                        />
                                                        <PaginationComponent
                                                            currentPage={inactiveDomainCurrentPage}
                                                            totalRecords={inactiveDomainTotalRecord}
                                                            itemsPerPage={inactiveDomainLimit}
                                                            setCurrentPage={setInactiveDomainCurrentPage}
                                                        />
                                                    </>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>

                <div className="aioa_dashboard-custom-branding-service-domain-list-save-btn" style={{ zIndex: 9 }}>
                    <Button type="submit" variant="primary" size="lg" onClick={(e) => HandleWhiteLabelStatusUpdate(e)}>
                        <>
                            {t("Save")}
                        </>
                    </Button>
                </div>
            </div>
        </>
    )
}

export default CustomBrandingService;